import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import useDateFormatter from 'src/hooks/useDateFormatter';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';

// Merge Person (its preview and its commit), Merge Families and Migrate Members
// refuse a move with 409 REPORT_ALREADY_HELD when the receiving family already
// holds one of the moving results' reports (TGCLOUD-1866). A family holds each
// report once, so the move cannot go ahead until a person marks one copy entered
// in error. The body is TestResultMoveConflictError.body() in
// backend/services/testResults/errors.js: the message says what was refused and
// how to resolve it, and `conflicts` names each report.
//
// Shown in the dialog the move was made from, not in the snackbar those screens
// use for other failures. The snackbar hides after three seconds, and on the
// pedigree page it sits under the application header; neither leaves time to
// note which reports have to be dealt with first.

export const REPORT_ALREADY_HELD = 'REPORT_ALREADY_HELD';

/**
 * The refusal an axios error carries, or null for any other failure, which the
 * caller reports exactly as it did before.
 *
 * @returns {{ message: string, conflicts: Array<{ reportDate?: string|null, laboratory?: string|null }> } | null}
 */
export const reportAlreadyHeldFrom = (err) => {
  const res = err?.response;
  if (res?.status !== 409 || res?.data?.code !== REPORT_ALREADY_HELD) {
    return null;
  }
  return {
    message: res.data.message || '',
    conflicts: Array.isArray(res.data.conflicts) ? res.data.conflicts : []
  };
};

// `reportDate` is the report's calendar date when it could be read, and the date
// AS PRINTED on the report when it could not (personTransfer.reportDateOf). Only
// the calendar date is reformatted; printed text is shown as printed.
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Each report by its date and laboratory, which is how a person finds it among
// the family's records. Not by result id: none of these screens can look one up.
const describe = (conflict, fmtDate) => {
  const raw = conflict.reportDate;
  const date = raw && CALENDAR_DATE.test(raw) ? fmtDate(raw) || raw : raw;
  const line = [
    date ? `Report dated ${date}` : 'Report date not recorded',
    conflict.laboratory || 'Laboratory not recorded'
  ].join(', ');
  // The one thing the list must add to the message: which held copy is an unfinished store.
  return conflict.heldRecordStatus === 'pending'
    ? translate(
        '{{line}} (the copy already held is an unfinished store that has to be cleared first)',
        { line }
      )
    : line;
};

export default function ReportAlreadyHeldAlert({ refusal, sx }) {
  const t = useAutoTranslation();

  const fmtDate = useDateFormatter();
  if (!refusal) return null;
  return (
    <Alert severity="error" variant="outlined" sx={{ borderRadius: 2, ...sx }}>
      <Typography variant="body2">{refusal.message}</Typography>
      {refusal.conflicts.length > 0 && (
        <Box
          component="ul"
          aria-label={t('Reports already held')}
          sx={{ mt: 1, mb: 0, pl: 2.5 }}
        >
          {refusal.conflicts.map((conflict, i) => (
            <Typography
              component="li"
              variant="body2"
              key={conflict.testResultId || i}
            >
              {describe(conflict, fmtDate)}
            </Typography>
          ))}
        </Box>
      )}
    </Alert>
  );
}
